import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { AGENTS } from "../data/agents";
import { Printer, ArrowLeft, ShieldCheck, AlertTriangle, Send } from "lucide-react";
import ShareDialog from "../components/ShareDialog";
import { HumanReviewCTA } from "../components/HumanReviewCTA";
import { useT } from "../lib/i18n";

export default function SessionReport() {
  const { t } = useT();
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/sessions/${id}`);
      setSession(r.data);
    })();
  }, [id]);

  if (!session) return <DashboardLayout title={t("Loading…")}><div className="text-[#94A3B8]">{t("Loading…")}</div></DashboardLayout>;

  const r = session.report || {};
  const lastRound = (session.round_outputs || []).slice(-1)[0];

  return (
    <DashboardLayout
      title={t("Decision report")}
      action={
        <div className="flex items-center gap-2 no-print">
          <Link to={`/sessions/${id}/workspace`} className="text-sm font-bold text-[#0B1B3D] inline-flex items-center gap-2 px-3 py-2 hover:underline"><ArrowLeft className="w-4 h-4" /> {t("Workspace")}</Link>
          <button data-testid="share-open-btn" onClick={() => setShareOpen(true)} className="bg-[#10B981] text-white hover:bg-[#059669] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2"><Send className="w-4 h-4" /> {t("Send to advisor")}</button>
          <button data-testid="export-pdf-btn" onClick={() => window.print()} className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2"><Printer className="w-4 h-4" /> {t("Export PDF")}</button>
        </div>
      }
    >
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl p-10 print-page" data-testid="report-content">
        <div className="flex items-center justify-between border-b border-slate-200 pb-6 mb-8">
          <div>
            <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">KONZENZO DECISION REPORT</p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B1B3D] mt-2 leading-tight">{session.title}</h1>
            <p className="text-sm text-[#475569] mt-2">{session.industry || "—"} · {session.country || "—"} · {session.company_size || "—"}</p>
          </div>
          <div className="text-right">
            <p className="font-mono-kz text-xs text-[#94A3B8]">{t("CONFIDENCE")}</p>
            <p className="font-display font-black text-3xl text-[#10B981]">{Math.round(r.decision_confidence || 0)}%</p>
          </div>
        </div>

        <Section title={t("Executive summary")}><p className="text-[#0B1B3D] text-base leading-relaxed">{r.executive_summary || "—"}</p></Section>
        <Section title={t("Consensus recommendation")}><div className="rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 p-5"><ShieldCheck className="w-5 h-5 text-[#10B981]" /><p className="mt-3 text-[#0B1B3D] leading-relaxed font-medium">{r.consensus_recommendation || "—"}</p></div></Section>
        <Section title={t("Business context")}><p className="text-[#475569]">{session.description}</p>{session.desired_outcome && (<p className="mt-3 text-[#475569]"><span className="font-bold text-[#0B1B3D]">{t("Desired outcome: ")}</span>{session.desired_outcome}</p>)}</Section>
        <Section title={t("Experts involved")}>
          <div className="grid sm:grid-cols-2 gap-3">
            {session.agent_ids.map((aid) => { const a = AGENTS.find((x) => x.id === aid); return a ? (<div key={aid} className="border border-slate-200 rounded-lg p-3"><p className="font-display font-bold text-[#0B1B3D]">{a.name}</p><p className="text-xs font-mono-kz text-[#94A3B8] tracking-widest mt-0.5">{a.specialty}</p></div>) : null; })}
          </div>
        </Section>
        <Section title={t("Areas of agreement")}><BulletList items={r.agreements} accent="text-[#10B981]" /></Section>
        <Section title={t("Areas of disagreement")}><BulletList items={r.disagreements} accent="text-[#F59E0B]" /></Section>
        <Section title={t("Risks & mitigations")}>
          <div className="space-y-3">
            {(r.risks_and_mitigations || []).map((row) => (
              <div key={`${row.risk}-${row.mitigation}`} className="border border-slate-200 rounded-lg p-4 grid md:grid-cols-2 gap-4">
                <div><p className="text-xs font-mono-kz text-[#EF4444] tracking-widest font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> RISK</p><p className="text-sm text-[#0B1B3D] mt-1">{row.risk}</p></div>
                <div><p className="text-xs font-mono-kz text-[#10B981] tracking-widest font-bold">MITIGATION</p><p className="text-sm text-[#0B1B3D] mt-1">{row.mitigation}</p></div>
              </div>
            ))}
            {(!r.risks_and_mitigations || r.risks_and_mitigations.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
          </div>
        </Section>
        <Section title={t("Action plan")}>
          <div className="space-y-2">
            {(r.action_plan || []).map((row, i) => (
              <div key={`${row.step}-${i}`} className="border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                <span className="font-mono-kz text-xs font-bold text-[#0EA5E9] tracking-widest pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1"><p className="text-sm font-bold text-[#0B1B3D]">{row.step}</p><p className="text-xs text-[#94A3B8] font-mono-kz mt-1 tracking-widest">{row.owner || "—"} · {row.timeframe || "—"}</p></div>
              </div>
            ))}
            {(!r.action_plan || r.action_plan.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
          </div>
        </Section>
        <Section title="Open questions"><BulletList items={r.open_questions} /></Section>
        <Section title="Final-round agent recommendations">
          <div className="space-y-2">
            {(lastRound?.agents || []).map((a) => (<div key={a.agent_id} className="border border-slate-200 rounded-lg p-3"><p className="font-display font-bold text-[#0B1B3D]">{a.agent_name}</p><p className="text-sm text-[#475569] mt-1">{a.recommendation}</p></div>))}
          </div>
        </Section>
        {r.suggested_next_review && <Section title={t("Suggested next expert review")}><p className="text-[#475569]">{r.suggested_next_review}</p></Section>}
        <p className="mt-12 pt-6 border-t border-slate-200 text-xs text-[#94A3B8] font-mono-kz">{t("Konzenzo provides structured AI analysis to support — not replace — qualified human review for tax, legal and financial matters.")}</p>
      </div>
      <HumanReviewCTA sessionId={id} />
      <ShareDialog sessionId={id} sessionTitle={session.title} open={shareOpen} onClose={() => setShareOpen(false)} />
    </DashboardLayout>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-display font-extrabold text-[#0B1B3D] text-xl mb-3">{title}</h2>
    {children}
  </section>
);

const BulletList = ({ items, accent = "text-[#0EA5E9]" }) => (
  <ul className="space-y-2">
    {(items || []).map((s) => (<li key={s} className="flex gap-2 text-[#475569]"><span className={`mt-1 ${accent}`}>•</span><span>{s}</span></li>))}
    {(!items || items.length === 0) && <p className="text-sm text-[#94A3B8]">—</p>}
  </ul>
);
